import moment from 'moment'
import I18n from '../../shared-lib/i18n/i18n'
import { $User } from '../stores/UserStore/UserStore'
import { $RootStore } from '../../src/stores/RootStore'

export const LiquidPropertyKeys = {
  CLIENT_FIRST_NAME: 'ClientFirstName',
  CLIENT_LAST_NAME: 'ClientLastName',
  BUSINESS_NAME: 'BusinessName',
  BUSINESS_EMAIL: 'BusinessEmail',
  BUSINESS_WEBSITE: 'BusinessWebsite',
  BOOKING_DATE_TIME: 'BookingDateTime',
  BOOKING_TIME: 'BookingTime',
  BOOKING_WEBSITE: 'BookingWebsite', // Deprecated
  BOOKING_LINK: 'BookingLink',
  LOYALTY_PROMOCODE: 'LoyaltyPromocode',
  AFTERCARE_LINK: 'AftercareLink',
  PAYMENT_AMOUNT: 'PaymentAmount',
  HEADER_LINK: 'HeaderLink',
  PAYMENT_LINK: 'PaymentLink',
  REVIEW_LINK: 'ReviewLink',
  GIFT_CARD_LINK: 'GiftCardLink',
  ASSIGNED_MEMBER: 'AssignedMember',
} as const

export type LiquidPropertiesKeyValues = typeof LiquidPropertyKeys[keyof typeof LiquidPropertyKeys];

export const dynamicValueDisplay: Record<LiquidPropertiesKeyValues, string> = {
  [LiquidPropertyKeys.CLIENT_FIRST_NAME]: 'Client first name',
  [LiquidPropertyKeys.CLIENT_LAST_NAME]: 'Client last name',
  [LiquidPropertyKeys.BUSINESS_NAME]: 'Business name',
  [LiquidPropertyKeys.BUSINESS_EMAIL]: 'Business email',
  [LiquidPropertyKeys.BUSINESS_WEBSITE]: 'Business website',
  [LiquidPropertyKeys.AFTERCARE_LINK]: 'Aftercare Link',
  [LiquidPropertyKeys.BOOKING_DATE_TIME]: 'Booking date time',
  [LiquidPropertyKeys.BOOKING_TIME]: 'Booking time',
  [LiquidPropertyKeys.LOYALTY_PROMOCODE]: 'Loyalty promocode',
  [LiquidPropertyKeys.PAYMENT_AMOUNT]: 'Payment amount',
  [LiquidPropertyKeys.BOOKING_LINK]: 'Booking link',
  [LiquidPropertyKeys.BOOKING_WEBSITE]: 'Booking link',
  [LiquidPropertyKeys.HEADER_LINK]: 'Header link',
  [LiquidPropertyKeys.PAYMENT_LINK]: 'Payment link',
  [LiquidPropertyKeys.REVIEW_LINK]: 'Review link',
  [LiquidPropertyKeys.GIFT_CARD_LINK]: 'Gift card link',
  [LiquidPropertyKeys.ASSIGNED_MEMBER]: 'Assigned member',
}

export type LiquidProperties = Record<LiquidPropertiesKeyValues, string>

export function createLiquidExampleData(
  user: $User,
  i18n: I18n,
  imagesLocation: string,
): LiquidProperties {
  const { currencyFormat } = i18n
  const fakeDate = moment().startOf('hour')

  const businessWebsite = 'https://booksolo.co/' + user.professionalProfile.saasProfileUrl

  return {
    'ClientFirstName': 'Jane',
    'ClientLastName': 'Doe',
    'BusinessName': user.professionalProfile.saasSettings.businessName || '',
    'BusinessEmail': user.professionalProfile.saasSettings.contactDetails?.find(t => t.type === 'EMAIL')?.value || user.emailAddress,
    'BusinessWebsite': businessWebsite,
    'BookingDateTime': fakeDate.format('DD MMM [at] HH:mm'),
    'BookingTime': fakeDate.format('HH:mm'),
    'LoyaltyPromocode': 'LOYAL1',
    'AftercareLink': businessWebsite + '/p/aftercare',
    'PaymentAmount': currencyFormat(1000),
    'BookingLink': 'https://booksolo.co/b/1234',
    'BookingWebsite': 'https://booksolo.co/b/1234', // It uses a short id when being generated on the API
    'HeaderLink': `${imagesLocation}saas/${user._id}-banner-horizontal.jpg`,
    'PaymentLink': 'https://booksolo.co/b/1234?a=p',
    'ReviewLink': 'https://booksolo.co/b/1234?a=r',
    'GiftCardLink': businessWebsite + '/giftcard',
    'AssignedMember': 'John Doe',
  }
}

export const liquifyKey = ((key: LiquidPropertiesKeyValues) => `{{${key}}}`)

export const replaceLiquidWithData = (text: string, data: Record<LiquidPropertiesKeyValues, string>): string => {
  let _text = text

  for (const key of Object.values(LiquidPropertyKeys)) {
    const _key = liquifyKey(key)
    _text = _text.replace(new RegExp(_key, 'g'), data[key])
  }

  return _text
}

export type LiquidOption = {
  value: LiquidPropertiesKeyValues
  label: string
  example: string
}

export function createLiquidOptions(
  store: $RootStore,
  {
    campaign = 'email',
    forSubject = false,
    extras = [],
  }: {
    campaign?: 'email' | 'sms'
    forSubject?: boolean
    extras?: LiquidPropertiesKeyValues[]
  } = {},
): LiquidOption[] {
  const {
    userStore,
    i18n,
    authStore,
    emailCampaignStore,
    smsCampaignStore,
    config: { imagesLocation },
  } = store

  const isTransactional = campaign === 'email'
    ? !!emailCampaignStore.editItem?.isTransactional
    : !!smsCampaignStore.editItem?.isTransactional

  const exampleData = createLiquidExampleData(userStore.data, i18n, imagesLocation)

  const keys: LiquidPropertiesKeyValues[] = [
    ...extras,
    LiquidPropertyKeys.CLIENT_FIRST_NAME,
    LiquidPropertyKeys.CLIENT_LAST_NAME,
    LiquidPropertyKeys.BUSINESS_NAME,
    ...(!forSubject ? [
      LiquidPropertyKeys.BUSINESS_WEBSITE,
      LiquidPropertyKeys.BUSINESS_EMAIL,
    ] : []),
    ...(isTransactional ? [
      LiquidPropertyKeys.BOOKING_DATE_TIME,
      ...(!forSubject ? [LiquidPropertyKeys.BOOKING_LINK] : []),
    ] : []),
    ...(authStore.isSalonSession && isTransactional ? [LiquidPropertyKeys.ASSIGNED_MEMBER] : []),
  ]

  return keys.map(option => ({
    value: option,
    label: dynamicValueDisplay[option],
    example: exampleData[option],
  }))
}
